import { useState, useEffect, useCallback, useMemo } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import {
  BUILD_ITEM_TYPE_FILTERS,
  BUILD_ITEM_TYPES,
  groupLabelsInOrder,
  templateMatchesTypeFilter,
} from '../../lib/functionalSpecSections';

const EMPTY_FORM = { name: '', help_text: '', build_item_types: null };

const STATE_OPTIONS = [
  { key: 'mandatory', label: 'Mandatory' },
  { key: 'optional', label: 'Optional' },
  { key: 'skip', label: 'Skip' },
];

export default function FunctionalSpecSections() {
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const orgId = profile?.organisation_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await sb
        .from('functional_spec_section_templates')
        .select('*')
        .eq('organisation_id', orgId)
        .order('order_index');
      if (error) throw error;
      setSections(data || []);
    } catch (e) {
      console.error('[FunctionalSpecSections] load failed', e);
      showToast('Failed to load functional spec sections');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.order_index - b.order_index),
    [sections],
  );

  const visibleSections = useMemo(
    () => sortedSections.filter((s) => templateMatchesTypeFilter(s, typeFilter)),
    [sortedSections, typeFilter],
  );

  const groupOrder = useMemo(() => groupLabelsInOrder(sortedSections), [sortedSections]);

  async function persistOrder(ordered) {
    setSaving(true);
    try {
      await Promise.all(ordered.map((s, i) =>
        sb.from('functional_spec_section_templates').update({ order_index: i }).eq('id', s.id)
      ));
      setSections(ordered.map((s, i) => ({ ...s, order_index: i })));
    } catch (e) {
      console.error('[FunctionalSpecSections] reorder failed', e);
      showToast('Failed to save order');
      load();
    } finally {
      setSaving(false);
    }
  }

  function onDrop(targetId) {
    if (!dragId || dragId === targetId) return;
    const ordered = [...sortedSections];
    const fromIdx = ordered.findIndex((s) => s.id === dragId);
    const toIdx = ordered.findIndex((s) => s.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    if (ordered[fromIdx].group_label !== ordered[toIdx].group_label) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    setDragId(null);
    setDragOverId(null);
    persistOrder(ordered);
  }

  async function setState(id, state) {
    const prev = sections.find((s) => s.id === id);
    if (!prev || prev.state === state) return;
    setSections((list) => list.map((s) => (s.id === id ? { ...s, state } : s)));
    try {
      const { error } = await sb
        .from('functional_spec_section_templates')
        .update({ state })
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error('[FunctionalSpecSections] state update failed', e);
      showToast('Failed to update section state');
      load();
    }
  }

  function startEdit(section) {
    setEditingId(section.id);
    setConfirmDeleteId(null);
    setEditForm({
      name: section.name,
      help_text: section.help_text || '',
      build_item_types: section.build_item_types?.length ? [...section.build_item_types] : null,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  }

  async function saveEdit(id) {
    const name = editForm.name.trim();
    const helpText = editForm.help_text.trim();
    if (!name) {
      showToast('Section name is required');
      return;
    }
    const types = editForm.build_item_types?.length ? editForm.build_item_types : null;
    setSaving(true);
    try {
      const payload = { name, help_text: helpText, build_item_types: types };
      const { error } = await sb
        .from('functional_spec_section_templates')
        .update(payload)
        .eq('id', id);
      if (error) throw error;
      setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...payload } : s)));
      setEditingId(null);
      showToast('Section saved', 'success');
    } catch (e) {
      console.error('[FunctionalSpecSections] save failed', e);
      showToast('Failed to save section');
    } finally {
      setSaving(false);
    }
  }

  function toggleTypeScope(type) {
    setEditForm((f) => {
      const current = f.build_item_types ? [...f.build_item_types] : [];
      if (current.includes(type)) {
        const next = current.filter((t) => t !== type);
        return { ...f, build_item_types: next.length ? next : null };
      }
      return { ...f, build_item_types: [...current, type] };
    });
  }

  function requestDelete(section) {
    if (!section.is_custom) {
      showToast('Platform sections cannot be removed — set to Skip instead');
      return;
    }
    setConfirmDeleteId(section.id);
    setEditingId(null);
  }

  async function deleteSection(id) {
    setSaving(true);
    try {
      const { error } = await sb
        .from('functional_spec_section_templates')
        .delete()
        .eq('id', id);
      if (error) throw error;
      const remaining = sortedSections.filter((s) => s.id !== id);
      await Promise.all(remaining.map((s, i) =>
        sb.from('functional_spec_section_templates').update({ order_index: i }).eq('id', s.id)
      ));
      setSections(remaining.map((s, i) => ({ ...s, order_index: i })));
      setConfirmDeleteId(null);
      showToast('Section removed', 'success');
    } catch (e) {
      console.error('[FunctionalSpecSections] delete failed', e);
      showToast(e?.code === '23503'
        ? 'This section is already used by functional specs. Set it to Skip instead.'
        : 'Failed to remove section');
    } finally {
      setSaving(false);
    }
  }

  async function addSection() {
    const appendicesRows = sortedSections.filter((s) => s.group_label === 'Appendices');
    const maxOrderInGroup = appendicesRows.reduce((m, s) => Math.max(m, s.order_index), -1);
    const insertAt = appendicesRows.length
      ? maxOrderInGroup + 1
      : sortedSections.reduce((m, s) => Math.max(m, s.order_index), -1) + 1;

    setSaving(true);
    try {
      const { data, error } = await sb
        .from('functional_spec_section_templates')
        .insert({
          organisation_id: orgId,
          group_label: 'Appendices',
          name: 'New section',
          help_text: '',
          state: 'optional',
          build_item_types: null,
          is_custom: true,
          order_index: insertAt,
        })
        .select()
        .single();
      if (error) throw error;

      const needsReorder = sortedSections.some((s) => s.order_index >= insertAt);
      if (needsReorder) {
        const reordered = [...sortedSections];
        reordered.splice(insertAt, 0, data);
        await Promise.all(reordered.map((s, i) =>
          sb.from('functional_spec_section_templates').update({ order_index: i }).eq('id', s.id)
        ));
        setSections(reordered.map((s, i) => ({ ...s, order_index: i })));
      } else {
        setSections((prev) => [...prev, data]);
      }
      startEdit(data);
    } catch (e) {
      console.error('[FunctionalSpecSections] add failed', e);
      showToast('Failed to add section');
    } finally {
      setSaving(false);
    }
  }

  function toggleGroup(label) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  if (loading) return <Loader text="Loading functional spec sections…" />;

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="empty" style={{ marginTop: 40 }}>Company admin access required.</div>
      </div>
    );
  }

  return (
    <div className="rt-shell">
      <div className="rt-page-head">
        <div className="rt-page-head-top">
          <div>
            <div className="rt-page-eyebrow">Admin · Workbench</div>
            <div className="rt-page-title">Functional Spec Sections</div>
          </div>
          <button type="button" className="btn primary" disabled={saving} onClick={addSection}>+ Add section</button>
        </div>
        <div className="rt-page-sub">
          The platform-standard section list every Functional Specification is built from. Set each section Mandatory, Optional, or Skip for this org — Skip removes it from the editor entirely. Sections scoped to a build item type only apply where relevant (e.g. Interface Design only shows on Interface-type build items).
        </div>
      </div>

      <div className="fss-toolbar">
        <span className="fss-toolbar-label">Filter by build item type</span>
        {BUILD_ITEM_TYPE_FILTERS.map((type) => (
          <button
            key={type || 'all'}
            type="button"
            className={`fss-type-filter-pill${typeFilter === type ? ' on' : ''}`}
            onClick={() => setTypeFilter(type)}
          >
            {type || 'All'}
          </button>
        ))}
        <div className="fss-legend-mini">
          <span><span className="fss-legend-dot fss-legend-mandatory" />Mandatory</span>
          <span><span className="fss-legend-dot fss-legend-optional" />Optional</span>
          <span><span className="fss-legend-dot fss-legend-skip" />Skip</span>
        </div>
      </div>

      <div className="fss-body-scroll">
        {sections.length === 0 && (
          <div className="fss-empty">No section templates yet. Run the platform seed migration for this organisation.</div>
        )}

        {groupOrder.map((groupLabel) => {
          const rows = visibleSections.filter((s) => s.group_label === groupLabel);
          if (!rows.length) return null;
          const isCollapsed = collapsedGroups.has(groupLabel);
          return (
            <div key={groupLabel} className="fss-group-block">
              <button
                type="button"
                className="fss-group-hdr"
                onClick={() => toggleGroup(groupLabel)}
              >
                <span className="fss-group-toggle">{isCollapsed ? '▸' : '▾'}</span>
                <span className="fss-group-title">{groupLabel}</span>
                <span className="fss-group-count">
                  {rows.length} section{rows.length === 1 ? '' : 's'}
                </span>
              </button>
              {!isCollapsed && (
                <div className="fss-section-list">
                  {rows.map((section) => {
                    const isEditing = editingId === section.id;
                    const isConfirming = confirmDeleteId === section.id;
                    const typeTag = section.build_item_types?.length
                      ? `${section.build_item_types.join(', ')} only`
                      : null;
                    return (
                      <div key={section.id}>
                        <div
                          className={`fss-section-row${dragId === section.id ? ' dragging' : ''}${dragOverId === section.id ? ' drag-over' : ''}`}
                          draggable
                          onDragStart={() => setDragId(section.id)}
                          onDragOver={(e) => { e.preventDefault(); if (section.id !== dragId) setDragOverId(section.id); }}
                          onDrop={(e) => { e.preventDefault(); onDrop(section.id); }}
                          onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                        >
                          <span className="fss-drag-handle" title="Drag to reorder">⠿⠿</span>
                          <div className="fss-section-info">
                            <div className="fss-section-name-line">
                              <span className="fss-section-name">{section.name}</span>
                              {section.is_custom
                                ? <span className="fss-tag fss-tag-custom">Custom</span>
                                : <span className="fss-tag fss-tag-platform">Platform</span>}
                              {typeTag && <span className="fss-tag fss-tag-typescope">{typeTag}</span>}
                            </div>
                            <div className="fss-section-help">{section.help_text || 'No help text set.'}</div>
                          </div>
                          <div className="fss-state-toggle">
                            {STATE_OPTIONS.map((opt) => (
                              <button
                                key={opt.key}
                                type="button"
                                className={`fss-state-btn fss-state-${opt.key}${section.state === opt.key ? ' on' : ''}`}
                                onClick={() => setState(section.id, opt.key)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          <div className="fss-row-actions">
                            <button type="button" className="btn sm icon ghost" title="Edit" onClick={() => startEdit(section)}>✎</button>
                            <button type="button" className="btn sm icon ghost" title="Delete" onClick={() => requestDelete(section)}>🗑</button>
                          </div>
                        </div>

                        {isConfirming && (
                          <div className="fss-delete-warning">
                            <span>
                              ⚠ Remove &quot;{section.name}&quot; from the platform standard?
                              Sections already used by a functional spec cannot be removed; set them to Skip instead.
                            </span>
                            <span className="fss-delete-warning-actions">
                              <button type="button" className="btn sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                              <button type="button" className="btn sm danger" disabled={saving} onClick={() => deleteSection(section.id)}>Remove</button>
                            </span>
                          </div>
                        )}

                        {isEditing && (
                          <div className="fss-edit-panel">
                            <div className="fss-edit-grid">
                              <div className="fss-edit-field">
                                <label>Name</label>
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                />
                              </div>
                              <div className="fss-edit-field">
                                <label>Help text</label>
                                <input
                                  type="text"
                                  value={editForm.help_text}
                                  onChange={(e) => setEditForm((f) => ({ ...f, help_text: e.target.value }))}
                                />
                              </div>
                            </div>
                            <div className="fss-edit-field fss-edit-field-scope">
                              <label>Scope to build item type(s) — leave none selected to apply to all types</label>
                              <div className="fss-type-chip-row">
                                {BUILD_ITEM_TYPES.map((type) => (
                                  <button
                                    key={type}
                                    type="button"
                                    className={`fss-type-chip${(editForm.build_item_types || []).includes(type) ? ' on' : ''}`}
                                    onClick={() => toggleTypeScope(type)}
                                  >
                                    {type}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="fss-edit-actions">
                              <button type="button" className="btn sm" onClick={cancelEdit}>Cancel</button>
                              <button type="button" className="btn sm primary" disabled={saving} onClick={() => saveEdit(section.id)}>Save</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {sections.length > 0 && visibleSections.length === 0 && (
          <div className="fss-empty">No sections match this filter.</div>
        )}
      </div>

      {toast && (
        <div className={`toast${toast.type === 'success' ? ' success' : ''}`} style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 300 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
